import React, { useCallback, useEffect, useState } from 'react'
import Hls from 'hls.js'

import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'

import { setRadioPlaying } from '@core/Areas/Navigation/Store'
import { selectIsRadioPlaying } from '@core/Areas/Navigation/Store/selectors'
import { setCloseSkyVideoPlayer } from '@core/Areas/SkyVideoPlayer/Store'
import { logError } from '@core/Utils'
import { useSubscribeToNative } from '@core/Utils/hooks/useSubscribeToNative'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'

// Audio Element is module scoped to allow play while using app
let globalAudioElement: HTMLAudioElement | null = null

const hls = Hls.isSupported()
  ? new Hls({
      enableWorker: true,
      debug: false,
    })
  : null

hls?.on(Hls.Events.ERROR, (_, data) => {
  logError(`Exception when attemping to play Radio (hls.js): ${data}`, true)
})

export function useRadio(isVisionPlaying: boolean): {
  handleRadioToggle(): void
  isRadioPlaying: boolean
  isRadioBusy: boolean
} {
  const isRadioPlaying = useAppSelector(selectIsRadioPlaying)
  const dispatch = useAppDispatch()
  const [isRadioBusy, setIsRadioBusy] = useState(false)
  const [audioElement, setAudioElement] = useState(globalAudioElement)
  const config = useRacingRadioProviderFeature()

  useSubscribeToNative(
    'NOTIFY_RADIO_STATE',
    data => {
      dispatch(setRadioPlaying(data.isPlaying))
      if (data.isPlaying) {
        dispatch(setCloseSkyVideoPlayer())
      }

      setIsRadioBusy(false)
    },
    [dispatch]
  )

  const pauseRadio = React.useCallback(() => {
    if (isReactNativeApp) {
      sendToNative('REQUEST_RADIO_STATE', { isPlaying: false })
    } else {
      if (audioElement) {
        audioElement.pause()

        // need to set the src to null or it just keeps downloading forever
        audioElement.src = ''
        audioElement.load()
        hls?.detachMedia()
        hls?.stopLoad()
      }
    }

    dispatch(setRadioPlaying(false))
  }, [audioElement, dispatch])

  // Stop Radio if Vision is playing
  useEffect(() => {
    if (isVisionPlaying) {
      pauseRadio()
    }
  }, [isVisionPlaying, dispatch, pauseRadio])

  // Synchronize Radio state with UI on mount
  useEffect(() => {
    if (isReactNativeApp) {
      sendToNative('REQUEST_RADIO_STATE')
      return undefined
    }

    if (!audioElement) {
      dispatch(setRadioPlaying(false))
      return undefined
    }

    dispatch(setRadioPlaying(!audioElement.paused && !!audioElement.currentTime))

    const onPauseEvent = () => dispatch(setRadioPlaying(false))
    audioElement.addEventListener('pause', onPauseEvent)

    const onPlayEvent = () => dispatch(setRadioPlaying(true))
    audioElement.addEventListener('play', onPlayEvent)

    return () => {
      audioElement?.removeEventListener('pause', onPauseEvent)
      audioElement?.removeEventListener('play', onPlayEvent)
    }
  }, [audioElement, dispatch])

  const triggerPlayOnAudioElement = useCallback(() => {
    if (!globalAudioElement) {
      return
    }

    setIsRadioBusy(true)
    globalAudioElement
      .play()
      .then(() => dispatch(setRadioPlaying(true)))
      .catch(e => {
        pauseRadio()
        logError(`Exception when attemping to play Radio: ${e}`, true)
      })
      .finally(() => setIsRadioBusy(false))
  }, [setIsRadioBusy, pauseRadio])

  const playWebRadio = useCallback(
    (streamUrl: string) => {
      if (!globalAudioElement) {
        globalAudioElement = new Audio(streamUrl)
        setAudioElement(globalAudioElement)
      }

      if (config.provider === 'RWWA' || !config.provider) {
        globalAudioElement.setAttribute('src', streamUrl)
        globalAudioElement.load()
        triggerPlayOnAudioElement()
      } else if (hls) {
        hls.loadSource(streamUrl)
        hls.attachMedia(globalAudioElement)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          triggerPlayOnAudioElement()
        })
      } else {
        logError('Could not start Radio - no supported decoding method', true)
      }
    },
    [triggerPlayOnAudioElement, setAudioElement]
  )

  const playRadio = useCallback(() => {
    if (!config.streamUrl) {
      logError('Could not start radio. No streaming url configured', true)
      return
    }

    if (isReactNativeApp) {
      playNativeRadio(config.streamUrl)
    } else {
      playWebRadio(config.streamUrl)
    }

    dispatch(setCloseSkyVideoPlayer())
  }, [dispatch, playNativeRadio, playWebRadio])

  const handleRadioToggle = useCallback(() => {
    if (isRadioPlaying) {
      pauseRadio()
    } else {
      playRadio()
    }
  }, [dispatch, pauseRadio, playRadio, isRadioPlaying])

  return {
    handleRadioToggle,
    isRadioPlaying,
    isRadioBusy,
  }
}

function useRacingRadioProviderFeature() {
  const config = useFeature(FeatureFlags.RACING_RADIO_PROVIDER.key)

  if (
    typeof config === 'object' &&
    typeof config.streamUrl === 'string' &&
    config.streamUrl.startsWith('https://')
  ) {
    return config
  } else {
    return { provider: undefined, streamUrl: undefined }
  }
}

function playNativeRadio(streamUrl: string) {
  // despite the name, this signal to native actually starts the player
  sendToNative('REQUEST_RADIO_STATE', {
    isPlaying: true,
    streamUrl,
  })
}
